import { useCallback, useEffect, useState } from 'react';
import type {
  OrganizationSport,
  PersonSportRating,
  PersonSportRatingHistoryEntry,
  PlayerRatingSettings,
} from '@court-sync/shared';
import { BarChart3, Loader2 } from 'lucide-react';
import { PersonSportRatingsEditor, loadOrganizationSportsForRatings, type PendingSportRating } from './OrgPlayerRatingSettingsTab';
import { playerRatingService } from '@/services/playerRating';
import { clubService } from '@/services/club';

export type { PendingSportRating };

interface PersonSportRatingsPanelProps {
  clubId: string;
  memberId?: string | null;
  draftMode?: boolean;
  onPendingRatingsChange?: (ratings: PendingSportRating[]) => void;
}

export function MemberPersonSportRatingsPanel({
  clubId,
  memberId = null,
  draftMode = false,
  onPendingRatingsChange,
}: PersonSportRatingsPanelProps) {
  const [settings, setSettings] = useState<PlayerRatingSettings | null>(null);
  const [sports, setSports] = useState<OrganizationSport[]>([]);
  const [ratings, setRatings] = useState<PersonSportRating[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const [settingsRes, clubRes] = await Promise.all([
        playerRatingService.getClubPlayerRatingSettings(clubId),
        clubService.getClub(clubId),
      ]);

      if (!settingsRes.success || !settingsRes.data) {
        setLoadError(settingsRes.error ?? 'Could not load player rating settings');
        return;
      }

      const orgId = clubRes.data?.organizationId;
      if (!orgId) {
        setLoadError('Organization not found for this club');
        return;
      }

      setSettings(settingsRes.data);

      const sportsList = await loadOrganizationSportsForRatings(orgId);
      setSports(sportsList);

      if (memberId) {
        const ratingsRes = await playerRatingService.getMemberSportRatings(clubId, memberId);
        if (ratingsRes.success && ratingsRes.data) {
          setRatings(ratingsRes.data);
        }
      } else {
        setRatings([]);
      }
    } catch {
      setLoadError('Could not load player ratings');
    } finally {
      setLoading(false);
    }
  }, [clubId, memberId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-dashed border-border bg-muted/30 px-4 py-6 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin shrink-0" aria-hidden />
        Loading player ratings…
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-200">
        {loadError}
      </div>
    );
  }

  if (!settings) {
    return null;
  }

  if (!settings.enabled) {
    return null;
  }

  return (
    <section className="rounded-xl border border-violet-200/80 bg-violet-50/40 dark:border-violet-900/40 dark:bg-violet-950/20">
      <div className="flex items-start gap-3 border-b border-violet-200/60 px-4 py-3 dark:border-violet-900/40">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-violet-100 dark:bg-violet-900/40">
          <BarChart3 className="h-4 w-4 text-violet-600 dark:text-violet-300" aria-hidden />
        </div>
        <div className="min-w-0">
          <h3 className="text-sm font-semibold text-foreground">{settings.label}</h3>
          <p className="mt-0.5 text-xs text-muted-foreground">
            Per-sport ratings for matchmaking. Operator-only — not shown on public boards.
            {draftMode ? ' Saved when you add this member.' : null}
          </p>
        </div>
      </div>
      <div className="p-4">
        <PersonSportRatingsEditor
          settings={settings}
          sports={sports}
          currentRatings={ratings}
          compact
          draftMode={draftMode}
          onDraftsChange={onPendingRatingsChange}
          onSaved={() => void load()}
          loadHistory={(organizationSportId) =>
            memberId
              ? playerRatingService.getMemberRatingHistory(clubId, memberId, {
                  organizationSportId,
                  limit: 20,
                  offset: 0,
                })
              : Promise.resolve([] as PersonSportRatingHistoryEntry[])
          }
          saveRatings={async (items) => {
            if (!memberId) return;
            await playerRatingService.updateMemberSportRatings(clubId, memberId, {
              ratings: items,
            });
          }}
        />
      </div>
    </section>
  );
}

interface StudentPersonSportRatingsPanelProps {
  academyId: string;
  organizationId: string;
  studentId?: string | null;
  draftMode?: boolean;
  onPendingRatingsChange?: (ratings: PendingSportRating[]) => void;
}

export function StudentPersonSportRatingsPanel({
  academyId,
  organizationId,
  studentId = null,
  draftMode = false,
  onPendingRatingsChange,
}: StudentPersonSportRatingsPanelProps) {
  const [settings, setSettings] = useState<PlayerRatingSettings | null>(null);
  const [sports, setSports] = useState<OrganizationSport[]>([]);
  const [ratings, setRatings] = useState<PersonSportRating[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const settingsRes = await playerRatingService.getAcademyPlayerRatingSettings(academyId);
      if (!settingsRes.success || !settingsRes.data) {
        setLoadError(settingsRes.error ?? 'Could not load player rating settings');
        return;
      }

      setSettings(settingsRes.data);
      const sportsList = await loadOrganizationSportsForRatings(organizationId);
      setSports(sportsList);

      if (studentId) {
        const ratingsRes = await playerRatingService.getStudentSportRatings(academyId, studentId);
        if (ratingsRes.success && ratingsRes.data) {
          setRatings(ratingsRes.data);
        }
      } else {
        setRatings([]);
      }
    } catch {
      setLoadError('Could not load player ratings');
    } finally {
      setLoading(false);
    }
  }, [academyId, organizationId, studentId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-dashed border-border bg-muted/30 px-4 py-6 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin shrink-0" aria-hidden />
        Loading player ratings…
      </div>
    );
  }

  if (loadError || !settings?.enabled) {
    return loadError ? (
      <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-200">
        {loadError}
      </div>
    ) : null;
  }

  return (
    <section className="rounded-xl border border-violet-200/80 bg-violet-50/40 dark:border-violet-900/40 dark:bg-violet-950/20">
      <div className="flex items-start gap-3 border-b border-violet-200/60 px-4 py-3 dark:border-violet-900/40">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-violet-100 dark:bg-violet-900/40">
          <BarChart3 className="h-4 w-4 text-violet-600 dark:text-violet-300" aria-hidden />
        </div>
        <div className="min-w-0">
          <h3 className="text-sm font-semibold text-foreground">{settings.label}</h3>
          <p className="mt-0.5 text-xs text-muted-foreground">
            Per-sport ratings for matchmaking. Operator-only — not shown on public boards.
          </p>
        </div>
      </div>
      <div className="p-4">
        <PersonSportRatingsEditor
          settings={settings}
          sports={sports}
          currentRatings={ratings}
          compact
          draftMode={draftMode}
          onDraftsChange={onPendingRatingsChange}
          onSaved={() => void load()}
          loadHistory={(organizationSportId) =>
            studentId
              ? playerRatingService.getStudentRatingHistory(academyId, studentId, {
                  organizationSportId,
                  limit: 20,
                  offset: 0,
                })
              : Promise.resolve([] as PersonSportRatingHistoryEntry[])
          }
          saveRatings={async (items) => {
            if (!studentId) return;
            await playerRatingService.updateStudentSportRatings(academyId, studentId, {
              ratings: items,
            });
          }}
        />
      </div>
    </section>
  );
}
